import * as React from "react";
import MuiIconDelete from "@mui/icons-material/Delete";
import { CreateUserResponseErr, Group, User } from "../../interface";
import { store } from "../../store";
import { lang } from "../../lang";
import { createUser } from "../../actions/createUser";
import { getGroups } from "../../actions/getGroups";
import { getUser } from "../../actions/getUser";
import { updateUser } from "../../actions/updateUser";
import { getMe } from "../../actions/getMe";
import { Button } from "../../components/atoms/Button";
import { Radio } from "../../components/atoms/Radio";
import { FlexHorizontal, FlexSpacer } from "../../components/atoms/Flex";
import { InputField } from "../../components/atoms/InputField";
import { Title } from "../../components/atoms/Text";
import { Popup } from "../../components/molecules/Popup";
import { catchEndpoint } from "../../catchEndpoint";

type HttpError = {
    response?: { status?: number };
    status?: number;
    message?: string;
    code?: string;
};

function isHttpError(error: unknown): error is HttpError {
    return typeof error === "object" && error !== null;
}

class CreateUser extends React.Component<{
    onClose: (ok?: boolean) => void;
    edit: User;
}> {
    public state = {
        email: "",
        emailError: "",
        isSuperuser: false,
        isStaff: false,
        isSupport: false,
        addedGroups: [] as string[],
        groupList: [] as Group[],
        initLoading: false,
        userLoading: false
    };
    public async componentDidMount(): Promise<void> {
        const { edit } = this.props;

        // Get Groups
        const groupList = await getGroups();
        this.setState({ groupList });

        // Load user Info
        if (edit && edit.id) {
            const userInfo = await getUser(edit.id);
            if (userInfo) {
                this.setState({
                    email: edit.username,
                    addedGroups: userInfo.groupnames,
                    isSuperuser: userInfo.is_superuser,
                    isStaff: userInfo.is_staff,
                    isSupport: userInfo.is_support
                });
            }
        }
    }
    public createUser = (e: React.FormEvent<HTMLFormElement>): void => {
        e.preventDefault();

        const { edit } = this.props;
        const { addedGroups, isSuperuser, isStaff, isSupport, groupList } = this.state;
        if (edit && edit.id && addedGroups) {
            this.setState({ userLoading: true });
            const groups = addedGroups.map((added) => {
                const foundGroup = groupList.find((group) => {
                    return group.name === added;
                });
                return foundGroup && foundGroup.url ? foundGroup.url : "";
            });
            updateUser(edit.id, isSuperuser, isStaff, isSupport, groups).finally(() => {
                this.setState({ userLoading: false });
                this.props.onClose(true);

                // Update latest information
                if (edit.id == store.user.id) {
                    getMe().catch(catchEndpoint);
                }
            });
        } else if (addedGroups) {
            try {
                this.setState({ userLoading: true });
                const groups = addedGroups.map((added) => {
                    const foundGroup = groupList.find((group) => {
                        return group.name === added;
                    });
                    return foundGroup && foundGroup.url ? foundGroup.url : "";
                });
                createUser(this.state.email, isSuperuser, isStaff, isSupport, groups)
                    .catch(catchEndpoint)
                    .finally(() => {
                        this.setState({ userLoading: false });
                        this.props.onClose(true);
                    });
            } catch (error) {
                if (isHttpError(error)) {
                    const err = error.response as CreateUserResponseErr;
                    if (err && err.username) {
                        this.setState({
                            userLoading: false,
                            emailError: String(lang[err.username[0]])
                        });
                    }
                }
            }
            this.setState({ userLoading: false });
        }
    };
    public formChange = (text: string, name: string): void => {
        const state = this.state;
        state[name] = text;
        this.setState(state);
    };
    public render(): React.ReactElement {
        //const { email, emailError, groupList, addedGroups, isSuperuser, isStaff, isSupport } = this.state;
        const { email, emailError, groupList, addedGroups } = this.state;
        const isLoading = this.state.initLoading || this.state.userLoading;

        return (
            <Popup
                onClose={() => {
                    this.props.onClose();
                }}
            >
                <Title className="title_create">{this.props.edit ? lang.editUser : lang.addUser}</Title>
                <form onSubmit={this.createUser}>
                    <InputField
                        readOnly={!!this.props.edit}
                        id="useremail"
                        data-at_id="user_email"
                        name="email"
                        autoFocus
                        title={lang.email}
                        type="text"
                        onChangeField={this.formChange}
                        value={email}
                        error={emailError}
                    />
                    <Radio
                        titleObject={{ isSuperuser: lang.isSuperuser, isStaff: lang.isStaff, isSupport: lang.isSupport }}
                        checkedObject={{ isSuperuser: this.state["isSuperuser"], isStaff: this.state["isStaff"], isSupport: this.state["isSupport"] }}
                        onClick={(e) => {
                            const target = e.target as HTMLInputElement;

                            const radioElement = document.querySelector(".radio-holder");
                            const inputList = radioElement?.querySelectorAll("input");

                            if (inputList) {
                                for (const input of inputList) {
                                    if (input.value === target.value) {
                                        target.checked = true;
                                        this.state[target.name] = true;
                                    } else {
                                        input.checked = false;
                                        this.state[input.name] = false;
                                    }
                                }

                                this.setState(this.state);
                            }
                        }}
                    />
                    <div className="create_user_holder">
                        {lang.permissions}
                        <select
                            className="create_user_select"
                            data-at_id="create_user_select"
                            value=""
                            onChange={(e) => {
                                addedGroups.push(e.target.value);
                                this.setState({ addedGroups });
                            }}
                        >
                            <option></option>
                            {groupList &&
                                groupList.map((g) => {
                                    const db = g.name.split("_")[0];
                                    const rgx = new RegExp(db + "_");
                                    if (addedGroups.find((e) => rgx.test(e))) {
                                        return null;
                                    }
                                    return (
                                        <option key={g.name} value={g.name}>
                                            {g.name}
                                        </option>
                                    );
                                })}
                        </select>
                    </div>
                    <div className="group_holder">
                        {addedGroups.map((g, i) => {
                            return (
                                <FlexHorizontal key={i} style={{ padding: "10px" }}>
                                    <div data-at_id="client_item">{g}</div>
                                    <FlexSpacer />
                                    <MuiIconDelete
                                        className="client_item_delete"
                                        onClick={() => {
                                            addedGroups.splice(i, 1);
                                            this.setState(addedGroups);
                                        }}
                                        data-at_id="client_item_delete"
                                    />
                                </FlexHorizontal>
                            );
                        })}
                    </div>
                    <FlexHorizontal>
                        <FlexSpacer />
                        <Button
                            id="createuserbtn"
                            data-at_id="create_user_button"
                            disabled={isLoading}
                            loading={isLoading}
                            submit
                            right
                            title={lang.add}
                            style={{ flex: 1, marginBottom: "-5px" }}
                        />
                    </FlexHorizontal>
                </form>
            </Popup>
        );
    }
}
export { CreateUser };
